import { useState } from "react";
import classes from "./MediaLibraryItem.module.less";
import type { MediaItem } from "~/MediaLibrary/types/mediaLibraryTypes";
import Tag from "~/common/components/Tag/Tag";
import { IconButton } from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import FavoriteBorderOutlinedIcon from "@mui/icons-material/FavoriteBorderOutlined";
import FavoriteOutlinedIcon from "@mui/icons-material/FavoriteOutlined";
import PlayCircleFilledWhiteOutlinedIcon from "@mui/icons-material/PlayCircleFilledWhiteOutlined";
import { downloadAssetFile } from "~/api/ApiHelper";
import { Snackbar, SnackbarCloseButton } from "~/common/m3/Snackbar/Snackbar";
import { useTranslation } from "react-i18next";
import BrokenImageOutlinedIcon from "@mui/icons-material/BrokenImageOutlined";
import FolderZipOutlinedIcon from "@mui/icons-material/FolderZipOutlined";
import { MEDIA_TYPES, MediaType } from "~/MediaLibrary/constants";
import { trackAssetInteraction } from "~/feature/Analytics/ga/mediaLibrary";

export default function MediaLibraryItem(props: {
  media: MediaItem;
  handleCardClick: (media: MediaItem) => void;
  handleFavouriteClick: (
    id: string,
    updatedFav: boolean,
    title?: string,
    language?: string,
    mediaType?: string,
  ) => void;
}) {
  const { media, handleCardClick, handleFavouriteClick } = props;
  const { id, previewUrl, mediaType, isFavourite, title, language, downloadUrl } = media;
  const [errorSnackbarOpen, setErrorSnackbarOpen] = useState(false);
  const { t } = useTranslation();

  const handleDownload = async () => {
    try {
      trackAssetInteraction("Content Download", id, title, language, mediaType);
      await downloadAssetFile(downloadUrl, title);
    } catch (err) {
      setErrorSnackbarOpen(true);
    }
  };

  const handleFavourite = async () => {
    await handleFavouriteClick(id, !isFavourite, title, language, mediaType);
  };

  const mediaActionOverLay = () => (
    <div className={classes.cardHeader}>
      {language && <Tag className={classes.langTag}>{language}</Tag>}
      <div className={classes.actions}>
        <IconButton
          variant="contained"
          size={"medium"}
          color={"secondary"}
          onClick={async (e) => {
            e.stopPropagation();
            e.preventDefault();
            handleDownload();
          }}
        >
          <FileDownloadOutlinedIcon />
        </IconButton>
        <IconButton
          variant="contained"
          size={"medium"}
          color={"secondary"}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            handleFavourite();
          }}
        >
          {isFavourite ? <FavoriteOutlinedIcon /> : <FavoriteBorderOutlinedIcon />}
        </IconButton>
      </div>
    </div>
  );

  const mediaPlayIcon = () => {
    return (
      MEDIA_TYPES[mediaType] == MediaType.VIDEO && (
        <IconButton
          className={classes.playIcon}
          variant="contained"
          size={"medium"}
          color={"secondary"}
          onClick={() => {}}
        >
          <PlayCircleFilledWhiteOutlinedIcon />
        </IconButton>
      )
    );
  };

  return (
    <>
      <div
        className={classes.item}
        key={id}
        onClick={() => {
          handleCardClick(media);
        }}
      >
        <div className={classes.card}>
          {mediaActionOverLay()}
          {mediaPlayIcon()}
          <ImageWithFallback previewUrl={previewUrl} mediaType={mediaType} className={classes.image} />
        </div>
        <h3 className={classes.title}>{title}</h3>
      </div>
      <Snackbar
        open={errorSnackbarOpen}
        autoHideDuration={3000}
        onClose={() => setErrorSnackbarOpen(false)}
        action={<SnackbarCloseButton onClick={() => setErrorSnackbarOpen(false)} />}
        message={t("media.library.dowloadFailed")}
      />
    </>
  );
}

export const ImageWithFallback = ({
  previewUrl,
  mediaType,
  className,
}: {
  previewUrl: string;
  mediaType: string;
  className: string;
}) => {
  const [loading, setLoading] = useState(true);
  const [imgError, setImgError] = useState(false);

  if (!previewUrl) {
    return fallbackImage();
  }

  if (MEDIA_TYPES[mediaType] === MediaType.ZIP) {
    return (
      <div className={classes.playIcon}>
        <FolderZipOutlinedIcon className={classes.unknownFile} />
      </div>
    );
  }

  return (
    <>
      {imgError && !loading ? (
        fallbackImage()
      ) : (
        <img
          src={previewUrl}
          alt={mediaType}
          className={className}
          onLoad={() => setLoading(false)}
          onError={() => {
            setLoading(false);
            setImgError(true);
          }}
        />
      )}
    </>
  );
};

const fallbackImage = () => {
  return (
    <div className={classes.playIcon}>
      <BrokenImageOutlinedIcon className={classes.unknownFile} />
    </div>
  );
};
